import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ARTICLES, CATEGORIES, CATEGORY_IMAGES } from "@/data/articles";
import SEO from "@/components/SEO";

export default function Learn() {
  const [cat, setCat] = useState("All");
  const shown = cat === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === cat);

  return (
    <div className="bg-cream min-h-[80vh]">
      <SEO title="Learning Center — Legacy Academy" description="Plain-English educational articles on wills, trusts, probate, life insurance, beneficiaries, and organizing important documents." path="/learn" />
      <div className="max-w-5xl mx-auto px-5 py-12 md:py-16">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-2xl bg-navy flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-gold" />
            </span>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-gold">Legacy Academy™</p>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-navy leading-tight">
            Learning Center
          </h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-2xl">
            Short, gentle reads on the topics families ask about most. No jargon, no pressure — just understanding.
          </p>
        </Reveal>

        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`min-h-[44px] rounded-full px-5 text-sm font-bold transition-colors ${
                cat === c ? "bg-navy text-cream" : "bg-white border border-sand text-slate-600 hover:border-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((a, i) => (
            <Reveal key={a.slug} delay={Math.min(i * 0.06, 0.3)}>
              <Link
                to={`/learn/${a.slug}`}
                data-testid="academy-article-card"
                className="block group bg-white rounded-3xl border border-sand overflow-hidden h-full transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/5"
              >
                {CATEGORY_IMAGES[a.category] && (
                  <div className="aspect-[16/7] overflow-hidden">
                    <img src={CATEGORY_IMAGES[a.category]} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                )}
                <div className="p-7">
                  <span className="inline-block text-xs font-bold uppercase tracking-widest text-gold bg-gold-light border border-sand rounded-full px-3 py-1">
                    {a.category}
                  </span>
                  <h2 className="mt-4 font-serif text-2xl font-semibold text-navy leading-snug">{a.title}</h2>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-gold">
                    {a.minutes}-minute read <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
